import { useSyncExternalStore } from 'react';

export type CallStatusState =
  | 'offline'
  | 'ready'
  | 'connecting'
  | 'ringing'
  | 'in-call'
  | 'ended'
  | 'error';

export interface CallStatusSnapshot {
  state: CallStatusState;
  direction: 'inbound' | 'outbound' | null;
  peerNumber: string | null;
  /**
   * Server-resolved caller identity for an inbound call (the `CallerDisplay`
   * custom parameter, e.g. "Jane Doe · (928) 555-1234"). Null for outbound
   * calls and for inbound legs that did not carry it.
   */
  peerDisplay: string | null;
  contactId: number | null;
  message: string | null;
  muted: boolean;
  startedAt: number | null;
  controls: CallStatusControls;
  updatedAt: number;
}

export interface CallStatusControls {
  canHangUp: boolean;
  canReject: boolean;
  canSendToVoicemail: boolean;
  canMute: boolean;
  canSendDigits: boolean;
  canAccept: boolean;
  hangUp: (() => void) | null;
  rejectCall: (() => void) | null;
  sendToVoicemail: (() => void) | null;
  toggleMute: (() => void) | null;
  sendDigit: ((digit: string) => void) | null;
  /** Accept the currently-ringing incoming call. Non-null only while
   *  the dialer device has a pending `incoming` event that hasn't been
   *  accepted/rejected/sent-to-voicemail yet. Consumers (e.g. the
   *  comms-page ringing overlay) call this to take the call without
   *  having to open the floating cockpit. */
  accept: (() => void) | null;
  openDialer: (() => void) | null;
}

const emptyControls: CallStatusControls = {
  canHangUp: false,
  canReject: false,
  canSendToVoicemail: false,
  canMute: false,
  canSendDigits: false,
  canAccept: false,
  hangUp: null,
  rejectCall: null,
  sendToVoicemail: null,
  toggleMute: null,
  sendDigit: null,
  accept: null,
  openDialer: null,
};

const idleSnapshot: CallStatusSnapshot = {
  state: 'offline',
  direction: null,
  peerNumber: null,
  peerDisplay: null,
  contactId: null,
  message: null,
  muted: false,
  startedAt: null,
  controls: emptyControls,
  updatedAt: Date.now(),
};

let currentSnapshot = idleSnapshot;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function setCallStatus(next: Partial<CallStatusSnapshot> & { state: CallStatusState }) {
  currentSnapshot = {
    ...currentSnapshot,
    ...next,
    controls: next.controls ?? currentSnapshot.controls,
    updatedAt: Date.now(),
  };
  emit();
}

export function clearCallStatus() {
  currentSnapshot = {
    ...idleSnapshot,
    state: 'ready',
    controls: emptyControls,
    updatedAt: Date.now(),
  };
  emit();
}

export function useCallStatus(): CallStatusSnapshot {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => currentSnapshot,
    () => idleSnapshot,
  );
}
